/*
 * Given function an array of keys and a Redux selector f, of the form (state, ...args) return a new function
 * that will call f with the same arguments, but only if the state or args have changed.
 *
 * The state has changed if any state[key] is not the same as the previous state[key] for any key in keys.
 * The args have changed if JSON.stringify(args) is not the same as the previously
 *
 * Example:
 *
 *  // It's expensive to call _geometriesForSelectedCanvas, and it changes "rarely" and each call with the same
 *  // inputs will return a *different* array filled with the same Geometries, so memoize!
 *  const geometriesForSelectedCanvas = memoizeReduxState(
 *      ['selectedCanvas', 'showArchivedGeometries', 'geometries'],
 *      _geometriesForSelectedCanvas
 *  )
 *
 * geometriesForSelectedCanvas(state) calls _geometriesForSelectedCanvas only if any of the following have changed:
 *
 * As a special case, if keys is an empty array, the entire state has to match
 *
 * - state.selectedCanvas
 * - state.showArchivedGeometries
 * - state.geometries
 */

import pick from './pick.js'

const memoizeReduxState = (keys, f) => {
    let previousState = {}
    let previousArgsStringified
    let previousValue

    const stateMatches = state => {
        // special case: match ENTIRE state
        if (keys.length === 0) return state === previousState

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i]
            if (state[key] !== previousState[key]) return false
        }
        return true
    }

    return (state, ...args) => {
        const newArgsStringified = JSON.stringify(args)
        const cantUsedMemoized = !stateMatches(state) || newArgsStringified !== previousArgsStringified

        if (cantUsedMemoized) {
            previousState = pick(keys, state)
            previousArgsStringified = newArgsStringified
            previousValue = f(state, ...args)
        }

        return previousValue
    }
}

/*
 *
 * Given an array of keys and a Redux selector f, of the form (state, id) return a new function
 * that will call f(state, id), but only if the state has changed. Values for *every* id are cached.
 *
 * The state has changed if any state[key] is not the same as the previous state[key] for any key in keys.
 * The args have changed if JSON.stringify(args) is not the same as the previously
 *
 * Example: it's expensive to call _updatesForCollaborationId, but it changes only if state.updates has changed
 *
 * const _updatesForCollaborationId = (state, id) => Object.values(state.updates).filter(u => u.collaborationId === id)
 *
 * So:
 *
 * const updatesForCollaborationId = memoizeReduxStateForMultipleIds(['updates'], _updatesForCollaborationId)
 *
 * will cache results of calling _updatesForCollaborationId(state, id) for *every* id, until state.updates changes
 *
 * Note: This function is subtly different from memoizeReduxState.
 *
 * - f(state, id) is called here to fill the cache, as opposed to f(state, id, ...args)
 * - previousValue[id] is stored for all ids that have previously called f -- until the state has changed
 */
const memoizeReduxStateForMultipleIds = (keys, f) => {
    let previousValue = {}
    let previousState = {}

    const stateMatches = state => {
        // special case: match ENTIRE state
        if (keys.length === 0) return state === previousState

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i]
            if (state[key] !== previousState[key]) return false
        }
        return true
    }

    return (state, id, ...args) => {
        const stateChanged = !stateMatches(state, id, ...args)

        // throw out the whole cache
        if (stateChanged) {
            previousValue = {}
            previousState = pick(keys, state)
        }

        // if there's no value for id, recompute it
        if (!previousValue[id]) previousValue[id] = f(state, id, ...args)

        return previousValue[id]
    }
}

export { memoizeReduxState, memoizeReduxStateForMultipleIds }
